import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileText, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tooltip } from '../../../components/ui/tooltip';
import PipeDataChart from './PipeDataChart';
import {
  PIPEFITTER_FETCH_PROJECT_REPORT,
  PIPEFITTER_SEND_EMAIL_PIPE_DATA,
} from '../../../redux/Pipefitter/types';
import { SendReportEmailModal } from './SendReportEmailModal';
import { Label } from '../../../components/ui/label';

/** Selected pipe from setup (e.g. extractPipeSizes) — may include nominal / OoR bounds for out-of-range highlighting */
export type PipeDataSelectedPipe = {
  pipeId?: string;
  displayName?: string;
  pipeName?: string;
  nominalId?: number;
  nominalOd?: number;
  nominalIdMin?: number;
  nominalIdMax?: number;
  nominalOdMin?: number;
  nominalOdMax?: number;
  oORIDTolerance?: number;
  oORODTolerance?: number;
  oORIDTolrence?: number;
  oORODTolrence?: number;
  /** Setup: tolerance (min/max) vs range-from-nominal — reserved for pipe data rules */
  highlightType?: 'tolerance' | 'Range';
  oORMin?: number;
  oORMax?: number;
  /** Same as setup extractPipeSizes (ID out-of-roundness band) */
  oORIDMin?: number;
  oORIDMax?: number;
  oORODMin?: number;
  oORODMax?: number;
};

interface PipeDataProps {
  pipeStatistics: any;
  pipeDataGraph: any[];
  isLoading: boolean;
  handleSwitchChange: (checked: boolean) => void;
  showGraphicalView: boolean;
  projectNumber: string;
  canReport: boolean;
  isShowIndividualPipeDetails?: boolean;
  /** Selected pipe supplies absolute ID/OD min–max (mm) and OoR tolerance for per-row highlighting */
  selectedPipe?: PipeDataSelectedPipe | null;
}

export const PipeData: React.FC<PipeDataProps> = ({
  pipeStatistics,
  pipeDataGraph,
  isLoading,
  handleSwitchChange,
  showGraphicalView,
  projectNumber,
  canReport,
  isShowIndividualPipeDetails = false,
  selectedPipe = null,
}) => {
  const dispatch = useDispatch();
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showIDData, setShowIDData] = useState(true);

  const pipeCounts = useSelector((state: any) => state.pipefitterProject || {});
  const pipesListFiltered: any[] = useSelector((state: any) => state.pipefitterReports?.pipesListFiltered ?? []) || [];
  const totalNumberOfPipes = pipeCounts.totalPipeCount || 0;
  const availablePipes = pipeCounts.pipeCount || 0;
  const usedPipes = pipeCounts.usedPipeCount || 0;

  const hasIDInFiltered = pipesListFiltered.some((p: any) => p.innerRadiusData && (p.avgID != null || p.maxID != null));
  const hasODInFiltered = pipesListFiltered.some((p: any) => p.outerRadiusData && (p.avgOD != null || p.maxOD != null));
  const formatMm = (v: number | null | undefined) => (v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '—');

  const nominalId = selectedPipe?.nominalId ?? 0;
  const nominalIdMin = selectedPipe?.nominalIdMin ?? 0;
  const nominalIdMax = selectedPipe?.nominalIdMax ?? 0;
  const nominalOdMin = selectedPipe?.nominalOdMin ?? 0;
  const nominalOd = selectedPipe?.nominalOd ?? 0;
  const nominalOdMax = selectedPipe?.nominalOdMax ?? 0;
  const oORIDTolrence =
    selectedPipe?.oORIDTolerance ?? selectedPipe?.oORIDTolrence ?? 0;
  const oORODTolrence =
    selectedPipe?.oORODTolerance ?? selectedPipe?.oORODTolrence ?? 0;

  const hasValidIdSpec =
    Boolean(selectedPipe) && nominalIdMax > nominalIdMin && Number.isFinite(nominalId) && nominalId > 0;
  const hasValidOdSpec =
    Boolean(selectedPipe) && nominalOdMax > nominalOdMin && Number.isFinite(nominalOd) && nominalOd > 0;

  const idTolBelow = nominalIdMin;
  const idTolAbove = nominalIdMax;
  const odTolBelow = nominalOdMin;
  const odTolAbove = nominalOdMax;

  console.log('idTolBelow', idTolBelow);
  console.log('idTolAbove', idTolAbove);

  const highlightClass = (out: boolean) => (out ? 'text-red-600 font-semibold' : '');

  const indivAvgClass = (
    v: number | null | undefined,
    idTolBelow?: number | null | undefined,
    idTolAbove?: number | null | undefined,
  ) => {
    if (!hasIDData()) return '';
    if (v == null || typeof v !== 'number' || !Number.isFinite(v)) return '';

    const out = v < (idTolBelow ?? 0) || v > (idTolAbove ?? 0);
    return highlightClass(out);
  };

  const indivMaxClass = (
    v: number | null | undefined,
    idTolAbove?: number | null | undefined,
  ) => {
    if (v == null || typeof v !== 'number' || !Number.isFinite(v)) return '';

    return highlightClass(v > (idTolAbove ?? 0));
  };

  const indivMinClass = (
    v: number | null | undefined,
    idTolBelow?: number | null | undefined,
  ) => {

    if (v == null || typeof v !== 'number' || !Number.isFinite(v)) return '';

    return highlightClass(v < (idTolBelow ?? 0));
  };

  /** Row OoR (max - min) vs setup OoR tolerance, or vs summary OoR +/- same tolerance in Range mode. */
  const indivOORClassFromRow = (
    v: number | null | undefined,
    maxValue: number | null | undefined,
    minValue: number | null | undefined,
    ooRTolrence?: number | null | undefined,
  ) => {

    if (v == null || typeof v !== 'number' || !Number.isFinite(v)) return '';
    return highlightClass(v > (ooRTolrence ?? 0));
  };

  const handleGeneratePDF = async () => {
    try {
      setIsPdfDownloading(true);
      dispatch({
        type: PIPEFITTER_FETCH_PROJECT_REPORT,
        payload: { projectNumber },
        cb: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${projectNumber}_Pipe-Data-Report.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsPdfDownloading(false);
        },
      });
    } catch (error) {
      setIsPdfDownloading(false);
      console.error('Error generating PDF:', error);
    }
  };

  const enableDownloadOrSendEmailButton = () => {
    return totalNumberOfPipes > 0;
  };

  const confirmSendEmail = async () => {
    setShowEmailModal(true);
  };

  const hasIDData = () => {
    return pipeStatistics && (
      pipeStatistics.avgIDAvg !== 0 ||
      pipeStatistics.maxIDAvg !== 0 ||
      pipeStatistics.minIDAvg !== 0
    );
  };

  const hasODData = () => {
    return pipeStatistics && (
      pipeStatistics.avgODAvg !== 0 ||
      pipeStatistics.maxODAvg !== 0 ||
      pipeStatistics.minODAvg !== 0
    );
  };

  const buildColumns = () => {
    const baseColumns: any[] = [
      {
        title: '',
        dataIndex: 'pipeno',
      },
    ];

    if (hasIDData()) {
      baseColumns.push(
        { title: 'Min(mm)', dataIndex: 'minid' },
        { title: 'Avg(mm)', dataIndex: 'avgid' },
        { title: 'Max(mm)', dataIndex: 'maxid' },
        { title: 'OoR(mm)', dataIndex: 'oor' }
      );
    }

    if (hasODData()) {
      baseColumns.push(
        { title: 'Min(mm)', dataIndex: 'minod' },
        { title: 'Avg(mm)', dataIndex: 'avgod' },
        { title: 'Max(mm)', dataIndex: 'maxod' },
        { title: 'OoR(mm)', dataIndex: 'oorod' }
      );
    }

    return baseColumns;
  };
  const buildColumnsFilter = () => {
    const baseColumns: any[] = [
      {
        title: 'Pipe No',
        dataIndex: 'pipeno',
      },
    ];

    if (hasIDData()) {
      baseColumns.push(
        { title: 'Avg ID', dataIndex: 'avgid' },
        { title: 'Max ID', dataIndex: 'maxid' },
        { title: 'Min ID', dataIndex: 'minid' },
        { title: 'OOR', dataIndex: 'oor' }
      );
    }

    if (hasODData()) {
      baseColumns.push(
        { title: 'Avg OD', dataIndex: 'avgod' },
        { title: 'Max OD', dataIndex: 'maxod' },
        { title: 'Min OD', dataIndex: 'minod' },
        { title: 'OOR OD', dataIndex: 'oorod' }
      );
    }

    return baseColumns;
  };
  const data = [
    {
      key: '1',
      pipeno: 'Avg (all)',
      minid: pipeStatistics?.avgIDMin || 0,
      avgid: pipeStatistics?.avgIDAvg || 0,
      maxid: pipeStatistics?.avgIDMax || 0,
      oor: pipeStatistics?.ooRAvg || 0,
      minod: pipeStatistics?.avgODMin || 0,
      avgod: pipeStatistics?.avgODAvg || 0,
      maxod: pipeStatistics?.avgODMax || 0,

      oorod: pipeStatistics?.ooRODAvg || 0,
    },
    {
      key: '2',
      pipeno: 'Max (all)',
      minid: pipeStatistics?.maxIDMin || 0,
      avgid: pipeStatistics?.maxIDAvg || 0,
      maxid: pipeStatistics?.maxIDMax || 0,
      oor: pipeStatistics?.ooRMax || 0,
      minod: pipeStatistics?.maxODMin || 0,
      avgod: pipeStatistics?.maxODAvg || 0,
      maxod: pipeStatistics?.maxODMax || 0,
      oorod: pipeStatistics?.ooRODMax || 0,
    },
    {
      key: '3',
      pipeno: 'Min (all)',
      minid: pipeStatistics?.minIDMin || 0,
      avgid: pipeStatistics?.minIDAvg || 0,
      maxid: pipeStatistics?.minIDMax || 0,
      oor: pipeStatistics?.ooRIDMin || 0,
      minod: pipeStatistics?.minODMin || 0,
      avgod: pipeStatistics?.minODAvg || 0,
      maxod: pipeStatistics?.minODMax || 0,
      oorod: pipeStatistics?.ooRODMin || 0,
    },
  ];

  const columns = buildColumns();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500">Loading pipe data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-6">

          <div className="space-y-4">
            <div className="space-y-4" style={{ background: 'whitesmoke', padding: '10px 20px', borderRadius: 12, border: '1px solid #e5e7eb' }}>

              <div className="space-y-4 pt-4">
                <h4 className="font-medium">Pipe Details</h4>
                {!selectedPipe ? (
                  <p className="text-sm text-muted-foreground">Select a pipe size to see nominal and out-of-roundness limits (mm).</p>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                      <p className="text-sm font-medium text-gray-900">Inner Diameter</p>
                      <div className="grid grid-cols-4 gap-3">

                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Minimum</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(selectedPipe.nominalIdMin)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Nominal ID</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(nominalId)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Maximum</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(selectedPipe.nominalIdMax)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground"> Out of roundness</Label>
                          <p className="text-sm font-semibold tabular-nums">
                            {formatMm(oORIDTolrence)} mm
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 rounded-md border border-gray-200 bg-white px-3 py-2">
                      <p className="text-sm font-medium text-gray-900"> Outer Diameter</p>
                      <div className="grid grid-cols-4 gap-3">

                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Minimum</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(selectedPipe.nominalOdMin)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Nominal OD</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(nominalOd)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground">Maximum</Label>
                          <p className="text-sm font-semibold tabular-nums">{formatMm(selectedPipe.nominalOdMax)} mm</p>
                        </div>
                        <div>
                          <Label className="text-xs font-normal text-muted-foreground"> Out of roundness</Label>
                          <p className="text-sm font-semibold tabular-nums">
                            {formatMm(oORODTolrence)} mm
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                )}
              </div>
            </div>
            <div className="space-y-4">
              {(hasIDData() || hasODData()) && (
                <div className="space-y-4" style={{ background: 'whitesmoke', padding: '10px 20px', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  {/* Header - matches graph component */}
                  <div style={{ textAlign: 'left', marginBottom: 8, maxWidth: '100%' }}>
                    <h3 className="text-base font-bold text-gray-900">Pipe Data - Summary</h3>
                    {/* <h1 style={{ color: 'black', fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
                    Pipe Data — Summary
                  </h1>
                  <p style={{ color: 'black', fontSize: 16, fontWeight: 500, margin: 0 }}>
                    Average, min and max <em>ID</em> and <em>OD</em> across all pipes
                  </p> */}
                  </div>

                  {/* Badges: Total / Available / Used - same as graph */}
                  {/* <div style={{ display: 'flex', gap: 20, marginBottom: 24, marginTop: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <div style={{ background: 'white', border: '2px solid #e04530', borderRadius: 10, padding: '8px 18px', textAlign: 'center' }}>
                    <div style={{ color: '#e04530', fontWeight: 700, fontSize: 14 }}>Total Pipes</div>
                    <div style={{ color: 'black', fontSize: 20, fontWeight: 800 }}>{totalNumberOfPipes}</div>
                  </div>
                  <div style={{ background: 'white', border: '2px solid #60b885', borderRadius: 10, padding: '8px 18px', textAlign: 'center' }}>
                    <div style={{ color: '#60b885', fontWeight: 700, fontSize: 14 }}>Available</div>
                    <div style={{ color: 'black', fontSize: 20, fontWeight: 800 }}>{availablePipes}</div>
                  </div>
                  <div style={{ background: 'white', border: '2px solid #718096', borderRadius: 10, padding: '8px 18px', textAlign: 'center' }}>
                    <div style={{ color: '#718096', fontWeight: 700, fontSize: 14 }}>Used</div>
                    <div style={{ color: 'black', fontSize: 20, fontWeight: 800 }}>{usedPipes}</div>
                  </div>
                </div> */}

                  {/* Table */}
                  <div className="overflow-x-auto" style={{ background: 'white', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead rowSpan={2} className="align-middle whitespace-nowrap min-w-[5rem]">
                            Statistic
                          </TableHead>
                          {hasIDData() && (
                            <TableHead colSpan={4} className="text-center">ID</TableHead>
                          )}
                          {hasODData() && (
                            <TableHead colSpan={4} className="text-center">OD</TableHead>
                          )}
                        </TableRow>
                        <TableRow>
                          {hasIDData() && (
                            <>
                              <TableHead>Min(mm)</TableHead>
                              <TableHead>Avg(mm)</TableHead>
                              <TableHead>Max(mm)</TableHead>
                              <TableHead>OoR(mm)</TableHead>
                            </>
                          )}
                          {hasODData() && (
                            <>
                              <TableHead>Min(mm)</TableHead>
                              <TableHead>Avg(mm)</TableHead>
                              <TableHead>Max(mm)</TableHead>
                              <TableHead>OoR(mm)</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.map((row: any) => (
                          <TableRow key={row.key} className="align-middle [&>td]:py-3 min-h-[44px]">
                            {columns.map((col: any, colIdx: number) => (
                              <TableCell
                                key={colIdx}
                                className={colIdx === 0 ? 'font-medium text-foreground whitespace-nowrap' : undefined}
                              //className={summaryCellClass(col.dataIndex, row[col.dataIndex])}
                              >
                                {colIdx === 0 ? row[col.dataIndex] : formatMm(row[col.dataIndex])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* 2. Individual Pipe Details — from pipesListFiltered */}
              {(hasIDInFiltered || hasODInFiltered) && pipesListFiltered.length > 0 && isShowIndividualPipeDetails && (
                <div className="space-y-3" style={{ background: 'whitesmoke', padding: '10px 20px', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  <h3 className="text-base font-bold text-gray-900">Individual Pipe Details</h3>
                  <div className="overflow-x-auto rounded-lg border border-gray-200" style={{ background: 'white' }}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-30">SI No</TableHead>
                          <TableHead>Pipe Profile Reference</TableHead>
                          {hasIDInFiltered && (
                            <TableHead colSpan={4} className="text-center">ID</TableHead>
                          )}
                          {hasODInFiltered && (
                            <TableHead colSpan={4} className="text-center">OD</TableHead>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableHead />
                          <TableHead />
                          {hasIDInFiltered && (
                            <>
                              <TableHead>Min(mm)</TableHead>
                              <TableHead>Avg(mm)</TableHead>
                              <TableHead>Max(mm)</TableHead>

                              <TableHead>OoR(mm)</TableHead>
                            </>
                          )}
                          {hasODInFiltered && (
                            <>
                              <TableHead>Min(mm)</TableHead>
                              <TableHead>Avg(mm)</TableHead>
                              <TableHead>Max(mm)</TableHead>
                              <TableHead>OoR(mm)</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pipesListFiltered.map((row: any, index: number) => (
                          <TableRow key={row.pipeEndID ?? index} className="border-b border-gray-200 align-middle min-h-[44px] [&>td]:py-3">
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{row.pipeEndID ?? '—'}</TableCell>
                            {hasIDInFiltered && (
                              <>
                                <TableCell className={indivMinClass(row.minID, idTolBelow)}>{formatMm(row.minID)}</TableCell>
                                <TableCell className={indivAvgClass(row.avgID, idTolBelow, idTolAbove)}>{formatMm(row.avgID)}</TableCell>
                                <TableCell className={indivMaxClass(row.maxID, idTolAbove)}>{formatMm(row.maxID)}</TableCell>
                                <TableCell className={indivOORClassFromRow(row.ooR, row.maxID, row.minID, oORIDTolrence)}>{formatMm(row.ooR)}</TableCell>
                              </>
                            )}
                            {hasODInFiltered && (
                              <>
                                <TableCell className={indivMinClass(row.minOD, odTolBelow)}>{formatMm(row.minOD)}</TableCell>
                                <TableCell className={indivAvgClass(row.avgOD, odTolBelow, odTolAbove)}>{formatMm(row.avgOD)}</TableCell>
                                <TableCell className={indivMaxClass(row.maxOD, odTolAbove)}>{formatMm(row.maxOD)}</TableCell>
                                <TableCell className={indivOORClassFromRow(row.ooROD, row.maxOD, row.minOD, oORODTolrence)}>{formatMm(row.oOROD)}</TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {pipeDataGraph?.length > 0 ? (
                <PipeDataChart
                  pipeDataGraph={pipeDataGraph}
                  showIDData={showIDData}
                  onIDODChange={setShowIDData}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">No data available</div>
              )
              }

            </div>
          </div>
        </CardContent>
      </Card>

      <SendReportEmailModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        projectNumber={projectNumber}
        title="Send Pipe Data Report"
        isSending={isSendingEmail}
        onSend={(emails) => {
          setIsSendingEmail(true);
          dispatch({
            type: PIPEFITTER_SEND_EMAIL_PIPE_DATA,
            payload: { projectNumber, emails },
            cb: () => {
              setShowEmailModal(false);
              setIsSendingEmail(false);
            },
            error: () => setIsSendingEmail(false),
          });
        }}
      />
    </>
  );
};
